import React, { useEffect, useState, useMemo } from 'react';
import { usePartnrStore } from '@/stores/partnrStore';
import { useCanvasStore } from '@/stores/canvasStore';

interface PartnrConnectionLinesProps {
  zoom: number;
  panX: number;
  panY: number;
}

const AUTO_DISMISS_MS = 8000; // Lines fade out after 8 seconds
const FADE_DURATION_MS = 600; // Fade-out animation duration

/** Get the center point of an item's nearest edge toward a target point */
function getEdgePoint(
  item: { position_x: number; position_y: number; width: number; height: number },
  targetX: number,
  targetY: number,
): { x: number; y: number } {
  const cx = item.position_x + item.width / 2;
  const cy = item.position_y + item.height / 2;
  
  // Determine which edge is closest to the target
  const dx = targetX - cx;
  const dy = targetY - cy;
  const absDx = Math.abs(dx);
  const absDy = Math.abs(dy);
  
  // Aspect ratio check for accurate edge selection
  const aspectRatio = item.width / item.height;
  
  if (absDx * (1 / aspectRatio) > absDy) {
    // Left or right edge
    if (dx > 0) {
      return { x: item.position_x + item.width, y: cy }; // Right edge
    } else {
      return { x: item.position_x, y: cy }; // Left edge
    }
  } else {
    // Top or bottom edge
    if (dy > 0) {
      return { x: cx, y: item.position_y + item.height }; // Bottom edge
    } else {
      return { x: cx, y: item.position_y }; // Top edge
    }
  }
}

/** Calculate a gentle bezier curve between two points */
function buildCurvePath(
  startX: number, startY: number,
  endX: number, endY: number,
): string {
  const dx = endX - startX;
  const dy = endY - startY;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const offset = Math.min(dist * 0.3, 60); // Control point offset

  // Perpendicular offset for a gentle arc
  const midX = (startX + endX) / 2;
  const midY = (startY + endY) / 2;
  
  // Use quadratic bezier for simplicity — single control point offset downward/rightward
  const cpX = midX + (dy > 0 ? -offset * 0.3 : offset * 0.3);
  const cpY = midY + offset * 0.2;

  return `M ${startX} ${startY} Q ${cpX} ${cpY} ${endX} ${endY}`;
}

export function PartnrConnectionLines({ zoom, panX, panY }: PartnrConnectionLinesProps) {
  const { canvasActionConnection, clearCanvasActionConnection } = usePartnrStore();
  const { items } = useCanvasStore();
  const [opacity, setOpacity] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  // Animate in when connection appears
  useEffect(() => {
    if (canvasActionConnection) {
      setIsVisible(true);
      // Small delay for mount, then fade in
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setOpacity(1));
      });

      // Auto-dismiss timer
      const dismissTimer = setTimeout(() => {
        setOpacity(0);
        setTimeout(() => {
          clearCanvasActionConnection();
          setIsVisible(false);
        }, FADE_DURATION_MS);
      }, AUTO_DISMISS_MS);

      return () => clearTimeout(dismissTimer);
    } else {
      setOpacity(0);
      const hideTimer = setTimeout(() => setIsVisible(false), FADE_DURATION_MS);
      return () => clearTimeout(hideTimer);
    }
  }, [canvasActionConnection, clearCanvasActionConnection]);

  // Calculate connection paths
  const paths = useMemo(() => {
    if (!canvasActionConnection) return [];

    const resultItem = items.find(i => i.id === canvasActionConnection.resultItemId);
    if (!resultItem) return [];

    const resultCenterX = resultItem.position_x + resultItem.width / 2;
    const resultCenterY = resultItem.position_y + resultItem.height / 2;

    return canvasActionConnection.sourceItemIds.map(sourceId => {
      const sourceItem = items.find(i => i.id === sourceId);
      if (!sourceItem) return null;

      const sourceCenterX = sourceItem.position_x + sourceItem.width / 2;
      const sourceCenterY = sourceItem.position_y + sourceItem.height / 2;

      // Get edge points (exit from source edge, enter at result edge)
      const sourceEdge = getEdgePoint(sourceItem, resultCenterX, resultCenterY);
      const resultEdge = getEdgePoint(resultItem, sourceCenterX, sourceCenterY);

      const path = buildCurvePath(sourceEdge.x, sourceEdge.y, resultEdge.x, resultEdge.y);

      return { path, id: sourceId, endX: resultEdge.x, endY: resultEdge.y };
    }).filter(Boolean) as { path: string; id: string; endX: number; endY: number }[];
  }, [canvasActionConnection, items]);

  if (!isVisible || paths.length === 0) return null;

  return (
    <svg
      className="absolute inset-0 pointer-events-none overflow-visible"
      style={{
        zIndex: 9, // Just below ArrowLayer (z-index: 10)
        transform: `scale(${zoom}) translate(${panX}px, ${panY}px)`,
        transformOrigin: 'top left',
        opacity,
        transition: `opacity ${FADE_DURATION_MS}ms ease-in-out`,
      }}
    >
      <defs>
        {/* Gradient for Partnr connection lines */}
        <linearGradient id="partnr-connection-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="hsl(var(--partnr))" stopOpacity="0.6" />
          <stop offset="50%" stopColor="hsl(var(--partnr-secondary))" stopOpacity="0.8" />
          <stop offset="100%" stopColor="hsl(var(--partnr))" stopOpacity="0.6" />
        </linearGradient>
        
        {/* Animated dot pattern for the connection */}
        <filter id="partnr-glow">
          <feGaussianBlur stdDeviation="2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {paths.map(({ path, id, endX, endY }) => (
        <g key={id}>
          {/* Glow layer (wider, blurred) */}
          <path
            d={path}
            fill="none"
            stroke="hsl(var(--partnr))"
            strokeWidth={4}
            strokeOpacity={0.15}
            filter="url(#partnr-glow)"
          />

          {/* Main dashed line */}
          <path
            d={path}
            fill="none"
            stroke="url(#partnr-connection-gradient)"
            strokeWidth={2}
            strokeDasharray="8,6"
            strokeLinecap="round"
            className="partnr-connection-line"
          />

          {/* Small dot at connection point on result item */}
          <circle
            cx={endX}
            cy={endY}
            r={4}
            fill="hsl(var(--partnr))"
            fillOpacity={0.8}
            className="partnr-connection-dot"
          />
        </g>
      ))}
    </svg>
  );
}
